<template>
    <v-container>
        <loading-spinner v-if="loading"/>
        <v-row v-else>
            <v-col cols="12" md="8">
                <v-row>
                    <v-col cols="12" md="4" align="center">
                        <avatar-profile :img="studentImg" with-back/>
                    </v-col>
                    <v-col cols="12" md="8">
                        <basic-info-profile :client="student"/>
                    </v-col>
                    <v-col cols="12">
                        <equipments-profile :equipments="equipments"/>
                    </v-col>
                    <v-col cols="12">
                        <student-statistics :docs="monthRoutines" />
                    </v-col>
                    <v-col cols="12">
                        <wellness-chart class="mx-0" :docs="monthRoutines" />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="4">
                <v-col cols="12">
                    <recent-chat-profile :student="student" />
                </v-col>
                <v-col cols="12">
                    <div class="section-title">
                        {{ $t('common.next_routines') }}
                    </div>
                    <div v-if="nextRoutines.length === 0" class="no-next-routines">
                        {{ $t('common.no_next_routines') }}
                    </div>
                    <routine-date-card
                        v-for="routine in nextRoutines"
                        :key="routine.id"
                        :routine="routine"
                    />
                    <v-btn class="b1" depressed text color="#016454" @click="toRoutines">
                        {{ $t('common.see_all') }}
                        <v-icon right>$right_arrow</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="12">
                    <div class="section-title">
                        {{ $t('common.injuries') }}
                    </div>
                    <injuries-card :student-id="studentId" readonly/>
                </v-col>
                <v-col cols="12" align="right">
                    <v-btn class="b1 b-underline" depressed text color="#078571" @click="showDeleteSubscription">
                        {{ $t('coach.delete_student_subscription') }}
                    </v-btn>
                </v-col>
            </v-col>
        </v-row>

        <!-- DELETE STUDENT FOR TRAINING -->
        <kimsa-confirm
            ref="deleteSubscriptionDialog"
            :title="$t('coach.delete_student_subscription_title')"
            :content="$t('coach.delete_student_subscription_body')"
            :loading="deletingSubscription"
            :persistent="false"
            @confirm="confirmSubscriptionDelete"
            :close-on-confirm="false"
        />

    </v-container>
</template>

<script>

import {db} from '@/utils/Firebase'
import {mapActions, mapState} from "vuex";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import AvatarProfile from "@/components/elements/profile/AvatarProfile";
import BasicInfoProfile from "@/components/elements/profile/BasicInfoProfile";
import EquipmentsProfile from "@/components/elements/profile/EquipmentsProfile";
import StudentStatistics from "@/components/student/StudentStatistics";
import WellnessChart from "@/components/statistics/WellnessChart";
import RecentChatProfile from "@/components/elements/profile/RecentChatProfile";
import RoutineDateCard from "@/components/common/cards/RoutineDateCard";
import InjuriesCard from "@/components/student/InjuriesCard";

export default {
    components: {
        InjuriesCard,
        RoutineDateCard,
        RecentChatProfile,
        WellnessChart,
        StudentStatistics,
        EquipmentsProfile,
        BasicInfoProfile,
        AvatarProfile,
        LoadingSpinner,
        KimsaConfirm,
    },
    data(vm) {
        return {
            loading: false,
            student: null,
            monthRoutines: [],
            deletingSubscription: false,
        }
    },
    computed: {
        ...mapState(['equipment']),
        studentId(vm = this) {
            return vm.$route.params.uid || null
        },
        studentImg(vm = this) {
            return vm.student?.avatar || vm.imgPlaceholder
        },
        equipments(vm = this) {
            if (!vm.student?.equipments) return []
            return vm.equipment?.filter(equipment => vm.student.equipments.includes(equipment.name)) || []
        },
        nextRoutines(vm = this) {
            return vm.monthRoutines
                ?.filter(routine => this.$moment(routine.rawDate.full).isSameOrAfter())
                ?.slice(0,5) || []
        }
    },
    async mounted() {
        const vm = this
        await vm.loadStudent()
        await vm.loadMonthRoutines()
        if (vm.equipment.length === 0) await vm.bindEquipment()
    },
    methods: {
        ...mapActions(['bindEquipment']),
        async loadStudent() {
            const vm = this
            vm.loading = true
            let studentRef = await db.collection('users').doc(vm.studentId).get()
            vm.student = {id: vm.studentId, ...studentRef.data()}
            vm.loading = false
        },
        async loadMonthRoutines() {
            const vm = this

            let startOfMonth = vm.$moment().startOf('month').format('YYYY-MM-DD')
            let endOfMonth = vm.$moment().endOf('month').format('YYYY-MM-DD')
            let monthRoutinesRef = await db.collection('assignedRoutines')
                .where('studentId', '==', vm.studentId)
                .where('rawDate.full', ">=", startOfMonth)
                .where('rawDate.full', "<=", endOfMonth)
                .orderBy('rawDate.full', 'asc').get()

            vm.monthRoutines = monthRoutinesRef.docs.map(routine => {
                return {id: routine.id, ...routine.data()}
            })
        },
        showDeleteSubscription() {
            const vm = this
            vm.$refs.deleteSubscriptionDialog.show()
        },
        async confirmSubscriptionDelete() {
            const vm = this

            vm.deletingSubscription = true

            let subscriptionId = vm.student?.studentSubscription?.id || null
            if(subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

            let payload = {studentSubscription: null}

            if(vm.student?.group) {
                payload.group = null
                let currentGroup = await db.collection('groups').doc(vm.student.group).get()
                let students = currentGroup.data().students.filter(st => st !== vm.studentId)
                await db.collection('groups').doc(vm.student.group).update({students: students})
            }
            if(vm.student?.team) {
                payload.team = null
                let currentTeamRef = await db.collection('teams').doc(vm.student.team.id).get()
                let currentTeam = {id: currentTeamRef.id, ...currentTeamRef.data()}
                let currentGroup = currentTeam?.groups?.find(group => vm.student.team.subgroupId === group.id)
                let students = currentGroup.students.filter(st => st !== vm.studentId)
                let groups = JSON.parse(JSON.stringify(currentTeam?.groups))
                let findGroupIndex = currentTeam?.groups?.findIndex(group => group.id === vm.student.team.subgroupId || null)
                groups[findGroupIndex].students = students
                await db.collection('teams').doc(vm.student.team.id).update({groups: groups})
            }

            await db.collection('users').doc(vm.studentId).update(payload)

            vm.deletingSubscription = false
            vm.$refs.deleteSubscriptionDialog.hide()
            vm.toast(vm.$t('coach.student_subscription_deleted'))

            await vm.$router.replace({
                name: 'coach.students',
                query: {deletedStudentAccount: vm.studentId}
            })
        },
        toRoutines(){
            const vm = this
            vm.$router.push({name: 'coach.calendar', query: {studentId: vm.studentId}})
        },
    },
}
</script>

<style scoped>

.section-title {
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
    color: #333333;
}

.no-next-routines {
    font-size: 14px;
    line-height: 17px;
    color: #333333;
    margin-top: 8px;
    text-align: center;
}

.b-underline {
    text-decoration: underline !important;
}

</style>
